import React, { useState, useEffect } from 'react';
import { 
  User, Phone, MapPin, Mail, Package, 
  CreditCard, Truck, Download, ChevronRight, Edit2,
  Shield, Bell, Globe, Save, X
} from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone_number: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  created_at: string;
}

interface Purchase {
  id: string;
  device_imei: string;
  device_serial: string;
  amount: number;
  currency: string;
  purchase_status: string;
  shipping_status: string;
  shipping_tracking_number: string;
  shipping_carrier: string;
  subscription_start_date: string;
  subscription_end_date: string;
  stripe_invoice_url: string;
  stripe_invoice_pdf: string;
  created_at: string;
  shipping_name: string;
  shipping_address_line1: string;
  shipping_city: string;
  shipping_state: string;
  shipping_postal_code: string;
  shipping_country: string;
}

const MyAccount: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'purchases' | 'settings'>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileData) {
        setProfile(profileData);
        setEditedProfile(profileData);
      } else if (profileError?.code === 'PGRST116') {
        // Profile doesn't exist, create one
        const newProfile = {
          id: user.id,
          email: user.email || '',
          full_name: user.user_metadata?.full_name || '',
          phone_number: '',
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US',
          created_at: new Date().toISOString()
        };
        
        const { data: createdProfile } = await supabase
          .from('user_profiles')
          .insert(newProfile)
          .select()
          .single();
        
        if (createdProfile) {
          setProfile(createdProfile);
          setEditedProfile(createdProfile);
        }
      }

      // Fetch purchases
      const { data: purchaseData } = await supabase
        .from('purchases')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (purchaseData) {
        setPurchases(purchaseData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editedProfile || !user) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: editedProfile.full_name,
          phone_number: editedProfile.phone_number,
          address_line1: editedProfile.address_line1,
          address_line2: editedProfile.address_line2,
          city: editedProfile.city,
          state: editedProfile.state,
          postal_code: editedProfile.postal_code,
          country: editedProfile.country,
        })
        .eq('id', user.id);

      if (!error) {
        setProfile(editedProfile);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const getShippingStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionStatus = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const daysLeft = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { text: 'Expired', color: 'text-red-600', days: 0 };
    } else if (daysLeft < 30) {
      return { text: `${daysLeft} days left`, color: 'text-yellow-600', days: daysLeft };
    } else {
      return { text: 'Active', color: 'text-green-600', days: daysLeft };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-traqpack-yellow"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gradient-to-br from-traqpack-yellow to-yellow-400 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
              <p className="text-gray-600 mt-1">{profile?.email}</p>
              <p className="text-sm text-gray-500 mt-1">
                Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
              activeTab === 'profile'
                ? 'text-traqpack-yellow border-b-2 border-traqpack-yellow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <User className="w-5 h-5 inline-block mr-2" />
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('purchases')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
              activeTab === 'purchases'
                ? 'text-traqpack-yellow border-b-2 border-traqpack-yellow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Package className="w-5 h-5 inline-block mr-2" />
            Purchase History
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
              activeTab === 'settings'
                ? 'text-traqpack-yellow border-b-2 border-traqpack-yellow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Shield className="w-5 h-5 inline-block mr-2" />
            Settings & Preferences
          </button>
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && profile && (
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-traqpack-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedProfile(profile);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name
                </label>
                <input
                  type="text"
                  value={editedProfile?.full_name || ''}
                  onChange={(e) => setEditedProfile(prev => prev ? {...prev, full_name: e.target.value} : null)}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'bg-white' : 'bg-gray-100'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={editedProfile?.phone_number || ''}
                  onChange={(e) => setEditedProfile(prev => prev ? {...prev, phone_number: e.target.value} : null)}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'bg-white' : 'bg-gray-100'
                  }`}
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={editedProfile?.address_line1 || ''}
                  onChange={(e) => setEditedProfile(prev => prev ? {...prev, address_line1: e.target.value} : null)}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'bg-white' : 'bg-gray-100'
                  }`}
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={editedProfile?.address_line2 || ''}
                  onChange={(e) => setEditedProfile(prev => prev ? {...prev, address_line2: e.target.value} : null)}
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    isEditing ? 'bg-white' : 'bg-gray-100'
                  }`}
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editedProfile?.city || ''}
                    onChange={(e) => setEditedProfile(prev => prev ? {...prev, city: e.target.value} : null)}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'bg-white' : 'bg-gray-100'
                    }`}
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={editedProfile?.state || ''}
                    onChange={(e) => setEditedProfile(prev => prev ? {...prev, state: e.target.value} : null)}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'bg-white' : 'bg-gray-100'
                    }`}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={editedProfile?.postal_code || ''}
                    onChange={(e) => setEditedProfile(prev => prev ? {...prev, postal_code: e.target.value} : null)}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'bg-white' : 'bg-gray-100'
                    }`}
                    placeholder="ZIP Code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Country
                  </label>
                  <select
                    value={editedProfile?.country || 'US'}
                    onChange={(e) => setEditedProfile(prev => prev ? {...prev, country: e.target.value} : null)}
                    disabled={!isEditing}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                      isEditing ? 'bg-white' : 'bg-gray-100'
                    }`}
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchases Tab */}
      {activeTab === 'purchases' && (
        <div className="space-y-6">
          {purchases.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Purchases Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven't made any purchases yet. Get your first TraqPack device today!
              </p>
              <button
                onClick={() => window.location.href = '/purchase'}
                className="bg-traqpack-yellow text-black font-bold px-6 py-3 rounded-lg hover:bg-yellow-400 transition-colors inline-flex items-center gap-2"
              >
                <Package className="w-5 h-5" />
                Buy TraqPack Device
              </button>
            </div>
          ) : (
            purchases.map((purchase) => {
              const subStatus = getSubscriptionStatus(purchase.subscription_end_date);
              
              return (
                <div key={purchase.id} className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        TraqPack Device - {purchase.device_imei}
                      </h3>
                      <p className="text-gray-600 mt-1">
                        Purchased on {new Date(purchase.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        ${purchase.amount.toFixed(2)} {purchase.currency}
                      </p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getShippingStatusColor(purchase.shipping_status)} mt-2`}>
                        {purchase.shipping_status === 'pending' ? 'Processing' :
                         purchase.shipping_status === 'shipped' ? 'Shipped' : 
                         'Delivered'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    {/* Device Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Device Information</h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-600">IMEI:</span> <span className="font-mono">{purchase.device_imei}</span></p>
                        <p><span className="text-gray-600">Serial:</span> <span className="font-mono">{purchase.device_serial || 'N/A'}</span></p>
                        <p><span className="text-gray-600">Status:</span> <span className="text-green-600 font-semibold">Active</span></p>
                      </div>
                    </div>

                    {/* Subscription Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Subscription</h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-gray-600">Status:</span>{' '}
                          <span className={`font-semibold ${subStatus.color}`}>{subStatus.text}</span>
                        </p>
                        <p><span className="text-gray-600">Expires:</span> {new Date(purchase.subscription_end_date).toLocaleDateString()}</p>
                        <p><span className="text-gray-600">Renewal:</span> $10/year</p>
                      </div>
                    </div>

                    {/* Shipping Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Shipping</h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600">
                          {purchase.shipping_address_line1}<br />
                          {purchase.shipping_city}, {purchase.shipping_state} {purchase.shipping_postal_code}
                        </p>
                        {purchase.shipping_tracking_number && (
                          <p>
                            <span className="text-gray-600">Tracking:</span>{' '}
                            <span className="font-mono text-blue-600">{purchase.shipping_tracking_number}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {purchase.stripe_invoice_url && (
                      <a
                        href={purchase.stripe_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <CreditCard className="w-4 h-4" />
                        View Invoice
                      </a>
                    )}
                    {purchase.stripe_invoice_pdf && (
                      <a
                        href={purchase.stripe_invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </a>
                    )}
                    {purchase.shipping_tracking_number && (
                      <button className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-colors">
                        <Truck className="w-4 h-4" />
                        Track Shipment
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Settings & Preferences</h2>
          
          <div className="space-y-6">
            {/* Notification Settings */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <Bell className="w-5 h-5 inline mr-2" />
                Notification Preferences
              </h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-gray-700">Email notifications for device alerts</span>
                  <input type="checkbox" defaultChecked className="toggle" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-gray-700">SMS notifications for critical alerts</span>
                  <input type="checkbox" defaultChecked className="toggle" />
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-gray-700">Monthly usage reports</span>
                  <input type="checkbox" defaultChecked className="toggle" />
                </label>
              </div>
            </div>

            {/* Security Settings */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <Shield className="w-5 h-5 inline mr-2" />
                Security
              </h3>
              <div className="space-y-3">
                <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between">
                  <span className="text-gray-700">Change Password</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between">
                  <span className="text-gray-700">Two-Factor Authentication</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Account Actions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
              <div className="space-y-3">
                <button className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between">
                  <span className="text-gray-700">Download Account Data</span>
                  <Download className="w-5 h-5 text-gray-400" />
                </button>
                <button className="w-full text-left px-4 py-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-between text-red-600">
                  <span>Delete Account</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;